import { addDaysIso } from '@rs/contracts'

/**
 * ══ ĐIỂM LẤY / ĐIỂM GIAO CỦA ĐƠN — luật thuần (chép `AzStop` · `azLastStops` · `azFreightDesc`) ══════
 */

export interface Stop {
  id: string
  /** `door` = tận kho · `station` = khách tự mang ra / tự nhận tại ga. */
  mode: 'door' | 'station'
  contact?: string
  phone?: string
  /** `YYYY-MM-DDTHH:mm` — ngày lấy/giao KẾ HOẠCH. */
  at?: string
  province?: string
  /** Phường/Xã — tên cột cũ `district` giữ nguyên (bản cũ bỏ cấp huyện nhưng giữ khoá). */
  district?: string
  address?: string
  station?: string
  note?: string
  actualAt?: string
}

/** Ngày giao KH tự = ngày lấy + 5 ngày (lịch chạy Nam–Bắc thường mất chừng đó — user 02/08). */
export const DELIVER_LEAD_DAYS = 5

/**
 * Điểm này có ĐÁNG LƯU không (bản cũ `keepStop`, `Az.tsx:4438`).
 *
 * ⚠ ĐO BẰNG "CÓ NƠI CHỐN HAY NGƯỜI NHẬN", cố ý KHÔNG tính `at`/`phone`/`note`: một điểm chỉ có
 *   mỗi cái ngày là điểm người dùng bắt đầu gõ rồi bỏ dở, không phải một điểm giao thật. Form mở
 *   sẵn một điểm trắng mỗi bên nên không lọc là phiếu nào cũng mang theo dòng trống.
 */
export const keepStop = (s: Stop): boolean =>
  (s.station ?? '') !== '' || (s.address ?? '') !== '' || (s.contact ?? '') !== '' || (s.province ?? '') !== ''

/**
 * Điểm này có được LƯU cùng ĐƠN HÀNG không — luật RIÊNG của form Đơn (bản cũ `OrderModal`, `Az.tsx:6733-6734`): ga · địa chỉ
 * · liên hệ, BA vế, KHÔNG xét Tỉnh. Khác `keepStop` (bốn vế) của form Báo giá (`Az.tsx:4438`) — bản cũ hai form hai luật.
 * ⚠ Điểm mới chỉ chọn Tỉnh/Phường (chưa ga, địa chỉ, liên hệ) bị BỎ khi lưu đơn, kể cả ngày lấy/giao — đúng bản cũ (anh chốt
 *   23/10, R1072–R1075). Trước 23/10 rs-erp dùng `keepStop` ở cả hai form.
 */
export const keepOrderStop = (s: Stop): boolean =>
  (s.station ?? '') !== '' || (s.address ?? '') !== '' || (s.contact ?? '') !== ''

let seq = 0
export const newStopId = (): string => {
  seq += 1
  return `s${Date.now().toString(36)}${String(seq)}`
}

export const blankStop = (at?: string): Stop => ({ id: newStopId(), mode: 'door', ...(at === undefined ? {} : { at }) })

const str = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined)

/** Đọc mảng điểm từ đơn (JSONB lỏng) về `Stop` — thiếu id thì cấp id mới. */
export const toStops = (raw: readonly unknown[] | null | undefined): Stop[] =>
  (raw ?? []).map((r) => {
    const o = (r ?? {}) as Record<string, unknown>
    const s: Stop = { id: str(o['id']) ?? newStopId(), mode: o['mode'] === 'station' ? 'station' : 'door' }
    for (const k of ['contact', 'phone', 'at', 'province', 'district', 'address', 'station', 'note', 'actualAt'] as const) {
      const v = str(o[k])
      if (v !== undefined) s[k] = v
    }
    return s
  })

/** Địa chỉ một dòng của điểm: địa chỉ, phường, tỉnh — không có thì lùi về ga. */
export const stopAddr = (s?: Stop): string =>
  [s?.address?.trim(), s?.district?.trim(), s?.province?.trim()].filter((x): x is string => x !== undefined && x !== '').join(', ')
  || (s?.station?.trim() ?? '')

/**
 * Tên điểm dùng làm ĐẦU TUYẾN của một dòng cước (bản cũ `stopRouteLabel`, `Az.tsx:998`).
 *
 * ⚠ ƯU TIÊN GA, ngược hẳn `stopAddr`. Hai hàm trả lời hai câu khác nhau: `stopAddr` là "xe chạy
 *   tới đâu" nên địa chỉ đường phố mới đúng; còn tuyến trên bảng giá và trên tờ báo giá gửi khách
 *   đi theo GA ("Ga Sóng Thần → Ga Giáp Bát"). Dùng nhầm hàm là dòng cước mang địa chỉ kho, rồi
 *   phép dò giá công bố không khớp được với dòng nào trong bảng giá.
 */
export const stopRouteLabel = (s?: Stop): string =>
  (s?.station?.trim() ?? '') || stopAddr(s)

const placeShort = (s?: Stop): string => {
  const wp = [s?.district?.trim(), s?.province?.trim()].filter((x): x is string => x !== undefined && x !== '').join(', ')
  if (wp !== '') return wp
  const parts = (s?.address ?? '').split(',').map((x) => x.trim()).filter((x) => x !== '')
  return parts.slice(-2).join(', ') || (s?.station?.trim() ?? '')
}

/** "Cước vận chuyển từ <Phường/Xã>, <Tỉnh> đến <Phường/Xã>, <Tỉnh>" — thiếu một đầu thì `''`. */
export const freightDesc = (pu?: Stop, dr?: Stop): string => {
  const a = placeShort(pu)
  const b = placeShort(dr)
  return a !== '' && b !== '' ? `Cước vận chuyển từ ${a} đến ${b}` : ''
}

/** Câu diễn giải có phải MÁY TỰ SINH không (được ghi đè khi đổi điểm) — `labelAuto === false` là người gõ tay. */
export const isAutoDesc = (l: { label: string; labelAuto?: boolean | undefined }): boolean =>
  l.labelAuto !== false && (l.label.trim() === '' || /^cước\s/i.test(l.label.trim()) || l.label.includes(' → '))

interface PastOrder {
  customerId: string
  createdAt: string
  routeName: string | null
  packId: string | null
  pickups: readonly unknown[] | null
  drops: readonly unknown[] | null
  pickupAddr: string | null
  dropAddr: string | null
}

type Place = Omit<Stop, 'id' | 'at' | 'actualAt'>

/**
 * ⭐ NHỚ ĐỊA CHỈ LẤY/GIAO THEO KHÁCH — điểm của đơn gần nhất CÙNG TUYẾN (ưu tiên cùng dịch vụ). Tuyến chưa
 * có đơn nào ⇒ rỗng (không điền địa chỉ tuyến khác). Không bê ngày giờ cũ.
 */
export const lastStops = (
  orders: readonly PastOrder[], customerId: string, routeName?: string, packId?: string,
): { pickup?: Place; drop?: Place } => {
  if (customerId === '') return {}
  const all = orders.filter((j) => j.customerId === customerId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const rn = (routeName ?? '').trim().toLowerCase()
  const pk = (packId ?? '').trim()
  const sameRoute = rn === '' ? all : all.filter((j) => (j.routeName ?? '').trim().toLowerCase() === rn)
  if (rn !== '' && sameRoute.length === 0) return {}
  const past = pk === '' ? sameRoute
    : [...sameRoute.filter((j) => (j.packId ?? '') === pk), ...sameRoute.filter((j) => (j.packId ?? '') !== pk)]
  const place = (raw: readonly unknown[] | null, addr: string | null): Place | undefined => {
    const first = toStops(raw)[0]
    if (first !== undefined) {
      const picked: Place = { mode: first.mode }
      for (const k of ['contact', 'phone', 'province', 'district', 'address', 'station', 'note'] as const) {
        const v = first[k]
        if (v !== undefined) picked[k] = v
      }
      return picked
    }
    return addr !== null && addr !== '' ? { mode: 'door', address: addr } : undefined
  }
  let pickup: Place | undefined
  let drop: Place | undefined
  for (const j of past) {
    pickup ??= place(j.pickups, j.pickupAddr)
    drop ??= place(j.drops, j.dropAddr)
    if (pickup !== undefined && drop !== undefined) break
  }
  return { ...(pickup === undefined ? {} : { pickup }), ...(drop === undefined ? {} : { drop }) }
}

/** Ngày giao mặc định theo ngày lấy. */
export const deliverDateFor = (pickupIso: string): string => addDaysIso(pickupIso, DELIVER_LEAD_DAYS)

/**
 * `YYYY-MM-DDTHH:mm` → `dd/mm/yyyy HH:mm`, bằng CẮT CHUỖI (bản cũ `fmtDT`, `Az.tsx:984`).
 *
 * ⚠ KHÔNG dùng `formatDateTimeVN` của `@rs/contracts`: hàm đó quy một MỐC THỜI GIAN UTC về giờ VN,
 *   còn giờ của điểm lấy/giao là GIỜ ĐỊA PHƯƠNG NGƯỜI NHẬP GÕ VÀO — không mang múi giờ nào. Cho nó
 *   qua `Date` là dịch đi 7 tiếng một con số vốn đã đúng.
 */
export const fmtStopAt = (iso: string): string => {
  const [d, tm] = iso.split('T')
  const [y, m, dd] = (d ?? '').split('-')
  if (dd === undefined || dd === '') return iso
  return `${dd}/${m}/${y}${tm === undefined ? '' : ` ${tm.slice(0, 5)}`}`
}
